import {
    Banner,
    BannerDescription,
    BannerLinks,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    Button,
    FlexBox,
    FluidImage,
    H2,
    Link,
    P,
    tokensRaw,
} from '@vp/swan';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { APP_LOCALE } from '@99designs/design-services-common';
import { FormattedHTMLMessage, SupportedLocale, __, __image } from '@99designs/i18n';
import { sendNavigationEvent, usePage } from '@99designs/tracking';
import { getNavigationData, useCrossPlatformTracking } from '../../..//products/tracking';
import { CloudinaryImage } from '../CloudinaryImage';
import type { DesignProductPresenter } from '../../../products/types';

interface BrandBanner {
    featuredProduct: DesignProductPresenter;
}

const StyledBannerImage = styled(FluidImage)`
    border-radius: ${tokensRaw.SwanSemBorderRadiusContainer};
`;

const ImageContainer = styled(FlexBox)`
    flex-basis: 50%;
`;

const StyledBanner = styled(Banner)`
    @media (max-width: ${tokensRaw.SwanBaseBreakpointXsEnd}) {
        flex-direction: column-reverse;
    }
`;

export function BrandBanner({ featuredProduct }: BrandBanner) {
    const { pageData } = usePage();
    const { locale = APP_LOCALE } = useRouter();

    const crossPlatformTracking = useCrossPlatformTracking(locale, 'Brand-Banner');
    const href = featuredProduct.ctaUrl(locale as SupportedLocale, crossPlatformTracking);

    return (
        <StyledBanner
            bgc="standard"
            variant="card"
            data-testid="brand-banner"
            textBoxHorizontalAlign="right"
        >
            <BannerTextContainer>
                <BannerText marginLeft="0">
                    <BannerTitle as={H2}>
                        <FormattedHTMLMessage
                            message={__('Your dream logo,<br />designed by a pro? Yes, please!')}
                        />
                    </BannerTitle>
                    <BannerDescription>
                        <P>
                            {__(
                                'Complete your brief, choose your designer, share your feedback and bring your dream logo to life.'
                            )}
                        </P>
                    </BannerDescription>
                    <BannerLinks>
                        <Button
                            skin="secondary"
                            render={(props) => (
                                <Link
                                    to={href}
                                    className={props.className}
                                    onClick={() => {
                                        sendNavigationEvent(
                                            getNavigationData(featuredProduct, {
                                                pageName: pageData.pageName,
                                                pageStage: pageData.pageStage,
                                                pageSection: pageData.pageSection,
                                                pagePath: '/design',
                                                pageZone: 'Brand Banner',
                                            })
                                        );
                                    }}
                                >
                                    {props.children}
                                </Link>
                            )}
                        >
                            {featuredProduct.translatedTitle()}
                        </Button>
                    </BannerLinks>
                </BannerText>
            </BannerTextContainer>
            <ImageContainer flexDirection="column" justifyContent="center" alignItems="flex-start">
                <CloudinaryImage
                    render={(props) => (
                        <StyledBannerImage
                            src={props.src}
                            srcSet={props.srcSet}
                            alt=""
                            role="presentation"
                        />
                    )}
                    imagePath={__image('expert_services/HubPage/brand-banner-image')}
                    width={936}
                />
            </ImageContainer>
        </StyledBanner>
    );
}
